import { ChevronLeftTwoTone, ChevronRightTwoTone } from '@mui/icons-material'
import {
  Badge,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  useTheme,
} from '@mui/material'
import { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'

import {
  DashboardPool,
  FolderPool,
  NewsPool,
  SensorPool,
  UserPool,
} from '@/types/domain'
import { KARMA_Route } from '@/types/karma/route'

import { Brand } from '../components/'
import {
  OperatorData,
  useDashboardPool,
  useFolderPool,
  useNewsPool,
  useNotificationPool,
  useOperator,
  useSensorPool,
  useUserPool,
} from '../hooks'
import routes from '../routes/routes'

import { DevReleaseInfo } from './DevReleaseInfo'

type MainMenuItemProps = {
  route: KARMA_Route
  collapsed: boolean
}

export const MainMenuItem = ({ route, collapsed }: MainMenuItemProps) => {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const theme = useTheme()

  const gotoPath = () => navigate(route.path)
  const color = theme.palette.white
  const selected = pathname.includes(route.path)

  return (
    <Tooltip title={collapsed ? route.name : ''} placement={'right'} arrow>
      <ListItemButton
        onClick={gotoPath}
        selected={selected}
        sx={{
          mb: 0.5,
          px: collapsed ? 0 : 2,
          justifyContent: collapsed ? 'center' : 'flex-start',
          minHeight: 48,
        }}
      >
        <ListItemIcon
          sx={{
            color,
            minWidth: 0,
            mr: collapsed ? 0 : 2,
            display: 'flex',
            justifyContent: 'center',
            width: collapsed ? '100%' : 'auto',
          }}
        >
          {route.icon}
        </ListItemIcon>

        <ListItemText
          primary={route.name}
          sx={{
            opacity: collapsed ? 0 : 1,
            transition: 'opacity 200ms ease',
            whiteSpace: 'nowrap',
          }}
        />
      </ListItemButton>
    </Tooltip>
  )
}

export const MainMenuItemAdv = ({ route, collapsed }: MainMenuItemProps) => {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const gotoPath = () => navigate(route.path)

  const theme = useTheme()
  const color = theme.palette.white

  const selected = pathname.includes(route.path)

  const dPool: DashboardPool = useDashboardPool()
  const sPool: SensorPool = useSensorPool()
  const uPool: UserPool = useUserPool()
  const fPool: FolderPool = useFolderPool()
  const nPool = useNotificationPool()
  const newsPool: NewsPool = useNewsPool()

  const countsByRouteName: Record<string, number> = {
    Dashboard: dPool.length,
    Sensoren: sPool.length,
    Benutzer: uPool.length,
    Gebäude: fPool.length,
    Benachrichtigungen: nPool.length,
    News: newsPool.length,
  }

  const badgeCount = countsByRouteName[route.name]

  return (
    <Tooltip title={collapsed ? route.name : ''} placement={'right'} arrow>
      <ListItemButton
        onClick={gotoPath}
        selected={selected}
        sx={{
          mb: 0.5,
          px: collapsed ? 0 : 2,
          justifyContent: collapsed ? 'center' : 'flex-start',
          minHeight: 48,
        }}
      >
        <ListItemIcon
          sx={{
            color,
            minWidth: 0,
            mr: collapsed ? 0 : 2,
            display: 'flex',
            justifyContent: 'center',
            width: collapsed ? '100%' : 'auto',
          }}
        >
          <Badge badgeContent={badgeCount} color={'secondary'} max={9999}>
            {route.icon}
          </Badge>
        </ListItemIcon>

        <ListItemText
          primary={route.name}
          sx={{
            color,
            opacity: collapsed ? 0 : 1,
            transition: 'opacity 200ms ease',
            whiteSpace: 'nowrap',
          }}
        />
      </ListItemButton>
    </Tooltip>
  )
}

type SidebarProps = {
  collapsed: boolean
  setCollapsed: React.Dispatch<React.SetStateAction<boolean>>
}

export const Sidebar = ({ collapsed, setCollapsed }: SidebarProps) => {
  const opData: OperatorData = useOperator()
  const theme = useTheme()
  const bgcolor = theme.palette.primary.main

  useEffect(() => {
    const saved = localStorage.getItem('sidebar-collapsed')
    if (saved !== null) setCollapsed(saved === 'true')
  }, [setCollapsed])

  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', String(collapsed))
  }, [collapsed])

  const drawerWidth = collapsed ? 72 : 260

  return (
    <Drawer
      anchor={'left'}
      open
      sx={{
        flexShrink: 0,
        position: 'relative',
        width: drawerWidth,
        '& .MuiDrawer-paper': {
          transition: 'left 200ms ease, background-color 150ms ease',
          width: drawerWidth,
          overflow: 'visible',
        },
      }}
      variant={'permanent'}
    >
      <Brand collapsed={collapsed} />

      <Tooltip
        title={collapsed ? 'Menü ausklappen' : 'Menü einklappen'}
        placement={'right'}
        arrow
      >
        <IconButton
          size={'small'}
          onClick={() => setCollapsed(prev => !prev)}
          sx={{
            //border: '1px solid #CCC',
            bgcolor,
            color: 'white',
            left: collapsed ? drawerWidth : drawerWidth - 72 / 2,
            position: 'absolute',
            top: 24,
            transform: 'translateX(-50%)',
            zIndex: 2000,
            width: 25,
            height: 25,
            '&:hover': { bgcolor: theme.palette.primary.dark },
          }}
        >
          {collapsed ? <ChevronRightTwoTone /> : <ChevronLeftTwoTone />}
        </IconButton>
      </Tooltip>

      <List sx={{ mt: 2, p: 0 }}>
        {routes.map(route => {
          const { hidden, isAdmin: adminRoute, redirect } = route

          const isHidden =
            hidden ||
            redirect ||
            (adminRoute && !opData?.isAdmin) ||
            (route.path === '/dashboard' && !opData?.dashboard_is_active)

          return isHidden ? null : (
            <MainMenuItemAdv
              key={route.path}
              route={route}
              collapsed={collapsed}
            />
          )
        })}
      </List>

      {!collapsed && <DevReleaseInfo />}
    </Drawer>
  )
}
